// src/pages/SupervisoryAssessments/SupervisoryAssessmentModal.js
import React, { useEffect, useState } from 'react';
import {
    CAlert,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CForm,
    CFormSelect,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableHeaderCell,
    CTableDataCell,
    CTableRow,
} from '@coreui/react';
import { apiClient } from 'services/Api';
import { SCORE_OPTIONS, GROUP_FIELDS, getGroupsForDepartment, buildInitialForm } from './questions';

const SupervisoryAssessmentModal = ({ visible, onClose, assessment, onSaved }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState(buildInitialForm(assessment || {}));

    useEffect(() => {
        if (assessment) {
            setForm(buildInitialForm(assessment));
            setError(null);
        }
    }, [assessment, visible]);

    if (!assessment) return null;

    const { department_name, evaluatee_name, year } = assessment;
    const isConcluded = !!assessment.concluded;
    const groups = getGroupsForDepartment(department_name);

    const onChangeScore = (field) => (e) => {
        if (isConcluded) return; // extra safety, though inputs are disabled
        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
        setForm((prev) => ({ ...prev, [field]: v }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (isConcluded) {
            // just ignore submits on concluded records
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const payload = {};
            GROUP_FIELDS.flat().forEach((field) => {
                payload[field] = form[field] ?? null;
            });

            await apiClient.post(`/supervisory-assessments/${assessment.id}/`, payload);
            onSaved?.();
            onClose();
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                err?.message ||
                'Erro ao guardar a avaliação.';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <CModal
            visible={visible}
            onClose={onClose}
            alignment="center"
            size="xl"
            backdrop="static"
            scrollable
        >
            <CModalHeader>
                <CModalTitle>
                    Avaliação de Chefias – {evaluatee_name} ({department_name}, {year})
                </CModalTitle>
            </CModalHeader>
            <CForm onSubmit={onSubmit}>
                <CModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {error && (
                        <CAlert color="danger" className="mb-3">
                            {error}
                        </CAlert>
                    )}

                    <CRow className="mb-3">
                        <CCol md={6}>
                            <strong>Avaliador:</strong> {assessment.evaluator_name}
                        </CCol>
                        <CCol md={3}>
                            <strong>Ano:</strong> {year}
                        </CCol>
                        <CCol md={3} className="text-end">
                            <strong>Estado:</strong>{' '}
                            {isConcluded ? (
                                <CBadge color="success">Concluída</CBadge>
                            ) : (
                                <CBadge color="secondary">Em aberto</CBadge>
                            )}
                        </CCol>
                    </CRow>

                    {groups.map((group, groupIndex) => (
                        <CCard className="mb-4" key={group.title}>
                            <CCardHeader>
                                <h5 className="mb-0">{group.title}</h5>
                            </CCardHeader>

                            <CCardBody>
                                <CTable responsive hover>
                                    <CTableBody>
                                        {group.questions.map((qText, qIndex) => {
                                            const fieldName = GROUP_FIELDS[groupIndex][qIndex];
                                            const currentValue = form[fieldName] ?? '';

                                            return (
                                                <CTableRow key={fieldName}>
                                                    <CTableHeaderCell className="col-10">
                                                        <label htmlFor={`sq-${fieldName}`} className="fw-normal">
                                                            {qText}
                                                        </label>
                                                    </CTableHeaderCell>

                                                    <CTableDataCell className="col-2 text-center">
                                                        <CFormSelect
                                                            id={`sq-${fieldName}`}
                                                            value={currentValue}
                                                            onChange={onChangeScore(fieldName)}
                                                            disabled={isConcluded || saving}
                                                        >
                                                            <option value="">—</option>
                                                            {SCORE_OPTIONS.map((val) => (
                                                                <option key={`${fieldName}-${val}`} value={val}>
                                                                    {val}
                                                                </option>
                                                            ))}
                                                        </CFormSelect>
                                                    </CTableDataCell>
                                                </CTableRow>
                                            );
                                        })}
                                    </CTableBody>
                                </CTable>
                            </CCardBody>
                        </CCard>
                    ))}
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" variant="ghost" onClick={onClose} disabled={saving}>
                        Fechar
                    </CButton>
                    {!isConcluded && (
                        <CButton color="primary" type="submit" disabled={saving}>
                            {saving ? <CSpinner size="sm" /> : 'Guardar'}
                        </CButton>
                    )}
                </CModalFooter>
            </CForm>
        </CModal>
    );
};

export default SupervisoryAssessmentModal;
